import { FC, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { $NavDrawerOpen } from '~/models/nav-drawer';
import { Link } from '@remix-run/react';
import { $SharePanelOpen } from '~/models/portal/share-panel';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useActiveToCurrentPage } from '~/utils/active/use-active-to-current-page';
import { $ShowBrandGuidanceInNav } from '~/utils/libra/brand-guidance';
import cls from 'classnames';
import { useTranslation } from 'react-i18next';
import { Debug } from '../debug';

import './style.scss';

export const NavBar: FC = () => {
  const { t } = useTranslation();

  const setShareOpen = useSetAtom($SharePanelOpen);
  const setDrawerOpen = useSetAtom($NavDrawerOpen);
  const { region } = useAtomValue($Locale);
  const active = usePageActive();
  const activeToCurrentPage = useActiveToCurrentPage();

  const showBrandGuidanceInNav = useAtomValue($ShowBrandGuidanceInNav);

  const logoHref = `/feed/foryou?region=${region}`;
  const searchHref = `/search/sug?region=${region}`;

  const handleClickLogo = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.top_bar_logo,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: 'feed',
            categoryId: 0,
          },
          ampExtra: {
            enter_position: ClickPosition.top_bar_logo,
          },
        });
      },
    },
    [active],
  );

  const logoObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.top_bar_logo,
      });
    }, []),
  });

  // const handleClickSearch = useReflowClick(
  //   {
  //     clickEvent: (_, bundle) => ({
  //       ...bundle,
  //       current_position: ClickPosition.top_bar_search,
  //     }),
  //     reflowBehavior: () => {
  //       activeToCurrentPage(ClickPosition.top_bar_search);
  //     },
  //   },
  //   [activeToCurrentPage],
  // );
  const handleClickSearch = useCallback(() => {
    window.Tea('wap_click', {
      current_position: ClickPosition.top_bar_search,
    });
  }, []);

  const searchObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.top_bar_search,
      });
    }, []),
  });

  const handleClickShare = useCallback(() => {
    window.Tea('wap_click', {
      current_position: ClickPosition.top_bar_share,
    });
    setShareOpen({});
  }, [active]);

  const shareObservedRef = useViewTracking<HTMLButtonElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.top_bar_share,
      });
    }, []),
  });

  const handleClickMenu = useCallback(() => {
    window.Tea('wap_click', {
      current_position: ClickPosition.top_bar_side,
    });
    setDrawerOpen(true);
  }, []);

  const menuObservedRef = useViewTracking<HTMLButtonElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.top_bar_side,
      });
    }, []),
  });

  return (
    <header className="navbar">
      <Link className="navbar_logo" onClick={handleClickLogo} to={logoHref} ref={logoObservedRef} />
      <div className={cls('navbar_title', { v2: showBrandGuidanceInNav })}>
        <span>Lemon8</span>
        {showBrandGuidanceInNav && <span className="guidance">{t('seo_banner_desc_lifestyle_community')}</span>}
      </div>
      {__BUILD_TYPE__ !== 'online' && <Debug />}
      <Link className="navbar_icon_item search" onClick={handleClickSearch} to={searchHref} ref={searchObservedRef} />
      <button className="navbar_icon_item share" onClick={handleClickShare} ref={shareObservedRef} />
      <button className="navbar_icon_item menu" onClick={handleClickMenu} ref={menuObservedRef} />
      <div className="border" />
    </header>
  );
};
